import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Calendar,
  Clock,
  MapPin,
  Users,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  Building2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NYC_BOROUGHS = ['Bronx', 'Brooklyn', 'Manhattan', 'Queens', 'Staten Island'];

const CommunityMeetings = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    neighborhood: '',
    borough: ''
  });

  const getText = (key, fallback) => {
    const texts = {
      en: {
        title: 'Community Meetings',
        subtitle: 'Connect with neighbors and participate in local advocacy',
        upcoming: 'Upcoming Meetings',
        noMeetings: 'No upcoming meetings scheduled',
        rsvp: 'RSVP',
        attending: 'Attending',
        cancelRsvp: 'Cancel RSVP',
        attendees: 'attendees',
        addMeeting: 'Add Meeting',
        editMeeting: 'Edit Meeting',
        deleteMeeting: 'Delete Meeting',
        deleteConfirm: 'Are you sure you want to delete this meeting?',
        deleteWarning: 'This action cannot be undone.',
        meetingTitle: 'Meeting Title',
        description: 'Description',
        date: 'Date',
        time: 'Time',
        location: 'Location',
        neighborhood: 'Neighborhood',
        borough: 'Borough',
        save: 'Save',
        cancel: 'Cancel',
        adminSection: 'Admin: Manage Meetings'
      },
      es: {
        title: 'Reuniones Comunitarias',
        subtitle: 'Conecta con vecinos y participa en defensa local',
        upcoming: 'Próximas Reuniones',
        noMeetings: 'No hay reuniones programadas',
        rsvp: 'Confirmar',
        attending: 'Asistiendo',
        cancelRsvp: 'Cancelar',
        attendees: 'asistentes',
        addMeeting: 'Agregar Reunión',
        editMeeting: 'Editar Reunión',
        deleteMeeting: 'Eliminar Reunión',
        deleteConfirm: '¿Estás seguro de que quieres eliminar esta reunión?',
        deleteWarning: 'Esta acción no se puede deshacer.',
        meetingTitle: 'Título de la Reunión',
        description: 'Descripción',
        date: 'Fecha',
        time: 'Hora',
        location: 'Ubicación',
        neighborhood: 'Vecindario',
        borough: 'Distrito',
        save: 'Guardar',
        cancel: 'Cancelar',
        adminSection: 'Admin: Gestionar Reuniones'
      },
      ar: {
        title: 'اجتماعات المجتمع',
        subtitle: 'تواصل مع الجيران وشارك في المناصرة المحلية',
        upcoming: 'الاجتماعات القادمة',
        noMeetings: 'لا توجد اجتماعات مجدولة',
        rsvp: 'تأكيد الحضور',
        attending: 'حاضر',
        cancelRsvp: 'إلغاء',
        attendees: 'حاضرين',
        addMeeting: 'إضافة اجتماع',
        editMeeting: 'تعديل الاجتماع',
        deleteMeeting: 'حذف الاجتماع',
        deleteConfirm: 'هل أنت متأكد من حذف هذا الاجتماع؟',
        deleteWarning: 'لا يمكن التراجع عن هذا الإجراء.',
        meetingTitle: 'عنوان الاجتماع',
        description: 'الوصف',
        date: 'التاريخ',
        time: 'الوقت',
        location: 'الموقع',
        neighborhood: 'الحي',
        borough: 'المنطقة',
        save: 'حفظ',
        cancel: 'إلغاء',
        adminSection: 'المسؤول: إدارة الاجتماعات'
      },
      ht: {
        title: 'Reyinyon Kominote',
        subtitle: 'Konekte ak vwazen epi patisipe nan defans lokal',
        upcoming: 'Reyinyon k ap Vini',
        noMeetings: 'Pa gen reyinyon pwograme',
        rsvp: 'Konfime',
        attending: 'W ap Patisipe',
        cancelRsvp: 'Anile',
        attendees: 'patisipan',
        addMeeting: 'Ajoute Reyinyon',
        editMeeting: 'Modifye Reyinyon',
        deleteMeeting: 'Efase Reyinyon',
        deleteConfirm: 'Èske ou sèten ou vle efase reyinyon sa a?',
        deleteWarning: 'Aksyon sa a pa ka defèt.',
        meetingTitle: 'Tit Reyinyon',
        description: 'Deskripsyon',
        date: 'Dat',
        time: 'Lè',
        location: 'Kote',
        neighborhood: 'Katye',
        borough: 'Distri',
        save: 'Sove',
        cancel: 'Anile',
        adminSection: 'Admin: Jere Reyinyon'
      }
    };
    return texts[language]?.[key] || texts.en[key] || fallback;
  };

  useEffect(() => {
    fetchMeetings();
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      if (token) {
        const response = await axios.get(`${API}/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(response.data.is_admin);
      }
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const fetchMeetings = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.get(`${API}/meetings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeetings(response.data.meetings || response.data || []);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      toast.error(language === 'es' ? 'Error al cargar reuniones' : 'Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (meetingId) => {
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/meetings/${meetingId}/rsvp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? 'RSVP confirmado' : 'RSVP confirmed!');
      fetchMeetings();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al confirmar' : 'Failed to RSVP');
    }
  };

  const handleCancelRSVP = async (meetingId) => {
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.delete(`${API}/meetings/${meetingId}/rsvp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? 'RSVP cancelado' : 'RSVP cancelled');
      fetchMeetings();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cancelar' : 'Failed to cancel RSVP');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.date || !formData.time || !formData.location) {
      toast.error(language === 'es' ? 'Complete los campos requeridos' : 'Please fill required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('polibot_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingMeeting) {
        await axios.put(`${API}/meetings/${editingMeeting.id}`, formData, { headers });
        toast.success(language === 'es' ? 'Reunión actualizada' : 'Meeting updated');
      } else {
        await axios.post(`${API}/meetings`, formData, { headers });
        toast.success(language === 'es' ? 'Reunión creada' : 'Meeting created');
      }

      setDialogOpen(false);
      setEditingMeeting(null);
      setFormData({ title: '', description: '', date: '', time: '', location: '', neighborhood: '', borough: '' });
      fetchMeetings();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save meeting');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (meeting) => {
    setEditingMeeting(meeting);
    setFormData({
      title: meeting.title || '',
      description: meeting.description || '',
      date: meeting.date || '',
      time: meeting.time || '',
      location: meeting.location || '',
      neighborhood: meeting.neighborhood || '',
      borough: meeting.borough || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (meetingId) => {
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.delete(`${API}/meetings/${meetingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? 'Reunión eliminada' : 'Meeting deleted');
      fetchMeetings();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al eliminar' : 'Failed to delete meeting');
    }
  };

  const openNewMeetingDialog = () => {
    setEditingMeeting(null);
    setFormData({ title: '', description: '', date: '', time: '', location: '', neighborhood: '', borough: '' });
    setDialogOpen(true);
  };

  const isUserAttending = (meeting) => {
    return meeting.attendees?.includes(user?.id);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : language === 'ar' ? 'ar-SA' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="meetings-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="meetings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="h-8 w-8 text-primary" />
            {getText('title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {getText('subtitle')}
          </p>
        </div>
      </div>

      {/* Admin Section */}
      {isAdmin && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              {getText('adminSection')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNewMeetingDialog} data-testid="add-meeting-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {getText('addMeeting')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>
                    {editingMeeting ? getText('editMeeting') : getText('addMeeting')}
                  </DialogTitle>
                  <DialogDescription>
                    {language === 'es' 
                      ? 'Complete los detalles de la reunión'
                      : 'Fill in the meeting details'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="title">{getText('meetingTitle')} *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        placeholder="Monthly Community Meeting"
                        data-testid="meeting-title-input"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="description">{getText('description')}</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows={2}
                        data-testid="meeting-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="date">{getText('date')} *</Label>
                        <Input
                          id="date"
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                          data-testid="meeting-date-input"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="time">{getText('time')} *</Label>
                        <Input
                          id="time"
                          type="time"
                          value={formData.time}
                          onChange={(e) => setFormData({...formData, time: e.target.value})}
                          data-testid="meeting-time-input"
                        />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="location">{getText('location')} *</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        placeholder="123 Community Center, Room 101"
                        data-testid="meeting-location-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="borough">{getText('borough')}</Label>
                        <Select 
                          value={formData.borough} 
                          onValueChange={(val) => setFormData({...formData, borough: val})}
                        >
                          <SelectTrigger data-testid="meeting-borough-select">
                            <SelectValue placeholder={getText('borough')} />
                          </SelectTrigger>
                          <SelectContent>
                            {NYC_BOROUGHS.map(b => (
                              <SelectItem key={b} value={b}>{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="neighborhood">{getText('neighborhood')}</Label>
                        <Input
                          id="neighborhood"
                          value={formData.neighborhood}
                          onChange={(e) => setFormData({...formData, neighborhood: e.target.value})}
                          placeholder="East Harlem"
                          data-testid="meeting-neighborhood-input"
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {getText('cancel')}
                    </Button>
                    <Button type="submit" disabled={submitting} data-testid="save-meeting-btn">
                      {submitting ? '...' : getText('save')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}

      {/* Meetings List */}
      <div>
        <h2 className="text-xl font-semibold mb-4">{getText('upcoming')}</h2>
        {meetings.length > 0 ? (
          <div className="grid gap-4">
            {meetings.map((meeting, index) => (
              <Card 
                key={meeting.id} 
                className="transition-all hover:shadow-lg hover:border-primary/20"
                data-testid={`meeting-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Calendar className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{meeting.title}</h3>
                          {meeting.description && (
                            <p className="text-sm text-muted-foreground mt-1">{meeting.description}</p>
                          )}
                          
                          <div className="flex flex-wrap gap-4 mt-3 text-sm">
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              {formatDate(meeting.date)}
                            </span>
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              {meeting.time}
                            </span>
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <MapPin className="h-4 w-4" />
                              {meeting.location}
                            </span>
                          </div>

                          {(meeting.borough || meeting.neighborhood) && (
                            <div className="flex items-center gap-2 mt-2">
                              {meeting.borough && (
                                <Badge variant="outline" className="flex items-center gap-1">
                                  <Building2 className="h-3 w-3" />
                                  {meeting.borough}
                                </Badge>
                              )}
                              {meeting.neighborhood && (
                                <Badge variant="secondary">{meeting.neighborhood}</Badge>
                              )}
                            </div>
                          )}

                          <div className="flex items-center gap-2 mt-3">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {meeting.attendee_count || 0} {getText('attendees')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      {isUserAttending(meeting) ? (
                        <Button 
                          variant="outline" 
                          onClick={() => handleCancelRSVP(meeting.id)}
                          className="border-green-500 text-green-600 hover:bg-green-50"
                          data-testid={`cancel-rsvp-${index}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {getText('attending')}
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleRSVP(meeting.id)}
                          data-testid={`rsvp-btn-${index}`}
                        >
                          {getText('rsvp')}
                        </Button>
                      )}

                      {isAdmin && (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => handleEdit(meeting)}
                            data-testid={`edit-meeting-${index}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="text-destructive hover:bg-destructive/10"
                                data-testid={`delete-meeting-${index}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{getText('deleteConfirm')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {getText('deleteWarning')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{getText('cancel')}</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDelete(meeting.id)}
                                  className="bg-destructive hover:bg-destructive/90"
                                >
                                  {getText('deleteMeeting')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium text-lg mb-2">{getText('noMeetings')}</h3>
              <p className="text-muted-foreground">
                {language === 'es' 
                  ? 'Vuelve pronto para ver próximas reuniones'
                  : 'Check back soon for upcoming meetings'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CommunityMeetings;
